import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { t } from "@/i18n/pl";
import { townLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import * as groupsService from "@/services/groups.service";
import type { PickupArea } from "@/types/api";

/**
 * Type-ahead picker over the official town gazetteer (~120k localities).
 * Search happens server-side; the Command list only renders what came back,
 * so selection is always a real, geocodable town — never free text.
 */
export function TownCombobox({
  value,
  onSelect,
  placeholder = t.createGroup.areaPlaceholder,
  id,
}: {
  value: PickupArea | null;
  onSelect: (area: PickupArea) => void;
  placeholder?: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Debounce keystrokes so we search once per pause, not per letter.
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: towns } = useQuery({
    queryKey: ["pickup-areas", debounced],
    queryFn: () => groupsService.listPickupAreas(debounced),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 5 * 60_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="text-foreground w-full justify-between font-normal"
        >
          <span className={cn("flex items-center gap-2 truncate", !value && "text-muted-foreground")}>
            <MapPin className="size-4 shrink-0 opacity-60" aria-hidden />
            {value ? townLabel(value) : placeholder}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        {/* Server-side search: the built-in client filter must stay off. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {debounced.trim().length < 2 ? t.createGroup.townSearchMin : t.createGroup.townSearchEmpty}
            </CommandEmpty>
            <CommandGroup>
              {(towns ?? []).map((town) => (
                <CommandItem
                  key={town.id}
                  value={town.id}
                  onSelect={() => {
                    onSelect(town);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("size-4", value?.id === town.id ? "opacity-100" : "opacity-0")}
                    aria-hidden
                  />
                  {townLabel(town)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
